import React, { useEffect, useState } from "react";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Form, FormInstance, Input, Typography, Checkbox, Switch } from "antd";
import { IDocumentList } from "store/process/process-model";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "../styled/form.styled";
import Document from "./document";
const { Title } = Typography;

type LayoutType = Parameters<typeof Form>[0]["layout"];

export interface IFormNewProcess {
  form: FormInstance;
  documentsList: Array<IDocumentList>;
  eventInboundActive: boolean;
  eventOutboundActive: boolean;
  visibleDrawerNewProcess: boolean;
  visibleDrawerEditProcess: boolean;
  visibleDocuments: boolean;
  allowImageCapture: boolean;
  processFetchDocumentsAsync: (...args) => void;
  processEventInboundActive: (...args) => void;
  processEventOutboundActive: (...args) => void;
  processUpdateVisibleDocuments: (...args) => void;
  processImageCapturesActive: (...args) => void;
}

const FormNewProcess = ({
  form,
  documentsList,
  visibleDrawerNewProcess,
  visibleDrawerEditProcess,
  allowImageCapture,
  processFetchDocumentsAsync,
  eventInboundActive,
  eventOutboundActive,
  processEventInboundActive,
  processEventOutboundActive,
  processUpdateVisibleDocuments,
  processImageCapturesActive,
  visibleDocuments,
}: IFormNewProcess) => {
  const [formLayout, setFormLayout] = useState<LayoutType>("vertical");
  const ENABLE_IMAGE_CAPTURE_TITLE = `${useTranslation({ id: "enable_image_capture_title" })}`;
  const ENABLE_IMAGE_CAPTURE_DESCRIPTION = `${useTranslation({ id: "enable_image_capture_description" })}`;

  const onFormLayoutChange = ({ layout }: { layout: LayoutType }) => {
    setFormLayout(layout);
  };

  useEffect(() => {
    if (visibleDrawerNewProcess) {
      form.setFieldsValue({ documents: undefined });
      processFetchDocumentsAsync();
    }
  }, []);

  useEffect(() => {
    if (visibleDrawerEditProcess) {
      processFetchDocumentsAsync();
    }
  }, []);

  const onChangeEventInbound = (e) => {
    processEventInboundActive(e.target.checked);
  };

  const onChangeEventOutbound = (e) => {
    processEventOutboundActive(e.target.checked);
  };

  const onChangeImageCaptures = (checked: boolean) => {
    processImageCapturesActive(checked);
  };

  return (
    <Form style={S.FormLayout} layout={"vertical"} form={form} initialValues={{ layout: formLayout }} onValuesChange={onFormLayoutChange} name="registers">
      <S.LayoutForm>
        <div className="item">
          <S.FormItem
            name="description"
            label={`${useTranslation({ id: "processes" })}*`}
            hasFeedback
            rules={[
              {
                required: true,
                message: useTranslation({ id: "required_field" }),
              },
            ]}
          >
            <Input maxLength={50} style={S.InputProcess} showCount data-testid="processDescription" placeholder={`${useTranslation({ id: "process_name" })}`} />
          </S.FormItem>
          <S.FormItem name="events" label={`${useTranslation({ id: "event_process_type" })}*`}>
            <S.EventsContainer>
              <div>
                <S.FormItemFieldName name="inbound">
                  <Checkbox checked={eventInboundActive} data-testid="eventInbound" onChange={onChangeEventInbound} />
                </S.FormItemFieldName>
                <Title level={5} style={S.CheckDescription}>
                  {useTranslation({ id: "inbound" })}
                </Title>
              </div>
              <div>
                <S.FormItemFieldName name="outbound">
                  <Checkbox checked={eventOutboundActive} data-testid="eventOutbound" onChange={onChangeEventOutbound} />
                </S.FormItemFieldName>
                <Title level={5} style={S.CheckDescription}>
                  {useTranslation({ id: "outbound" })}
                </Title>
              </div>
            </S.EventsContainer>
          </S.FormItem>
          <Document
            documentsList={documentsList}
            eventInboundActive={eventInboundActive}
            eventOutboundActive={eventOutboundActive}
            processUpdateVisibleDocuments={processUpdateVisibleDocuments}
            visibleDocuments={visibleDocuments}
          />
          <div>
            <S.FormItem name="allowImageCapture" label={ENABLE_IMAGE_CAPTURE_TITLE}>
              <S.ImageCapturesDescription>{ENABLE_IMAGE_CAPTURE_DESCRIPTION}</S.ImageCapturesDescription>
              <Switch
                className="switch-component"
                data-testid="activeImageCaptures"
                checked={allowImageCapture}
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
                onChange={onChangeImageCaptures}
              />
            </S.FormItem>
          </div>
        </div>
      </S.LayoutForm>
    </Form>
  );
};

export default FormNewProcess;
